// Client for the public PROVIDER APPLICATION endpoints. Kept separate from api.ts
// (the patient funnel client) so the two flows never entangle. Types mirror the
// server contract in expy-health-api (feat/provider-onboarding-backend):
//
//   POST /public/provider-application/start   {email, npi?, country?} → {sent, applyLink?, token?}
//     (npi/country: the directory claim hand-off, see lib/claimHandoff.ts; the
//     server seeds a new draft with them and copies the identity it already
//     holds for that email onto blank fields)
//   GET  /public/provider-application?token=… → {application} (404 unknown, 410 expired/reviewed)
//   POST /public/provider-application         {token, ...fields, submit?} → {saved, status}
//
// In dev, VITE_MOCK_PROVIDER_API=1 swaps in a localStorage-backed mock so the full
// UI flow is walkable before the API branch is deployed. The mock is DEV-only.
import { ApiError, RateLimited } from './api';
import type { ClaimHandoff } from './claimHandoff';

const BASE = import.meta.env.VITE_API_BASE_URL?.replace(/\/$/, '') || '';

export interface ExpertiseArea {
  title: string;
  description: string;
}

// Every writable field on the application. All optional — drafts are partial by design.
export interface ProviderApplicationFields {
  firstName?: string;
  lastName?: string;
  phone?: string;
  npi?: string;
  licenseNumber?: string;
  licenseExpiresOn?: string; // YYYY-MM-DD
  licensedStates?: string[]; // full state names, e.g. "California"
  compactPrivileges?: string[];
  specialties?: string[];
  conditionsTreated?: string[];
  languages?: string[];
  yearsInPractice?: number;
  bio?: string;
  expertiseAreas?: ExpertiseArea[];
  acceptedInsurancePlans?: string[];
  clinicLocation?: string;
  cashRate?: number;
  sessionDurations?: number[]; // minutes
  malpracticeAttested?: boolean;
  malpracticeCarrier?: string;
  malpracticeExpiresOn?: string; // YYYY-MM-DD
  country?: 'US' | 'GB' | 'OTHER';
  // Optional profile-presentation fields — never part of the submit gate.
  credential?: string; // e.g. "DPT", "PT, OCS"
  headline?: string; // one line shown under the name
  practiceName?: string;
  source?: Record<string, unknown>;
}

export interface ProviderApplication extends ProviderApplicationFields {
  email?: string;
  status?: string; // server-owned
  // The stored headshot key (server-owned) and a short-lived signed URL the
  // server mints on read so the pre-auth widget can preview it on resume.
  photoFileId?: string;
  photoUrl?: string;
}

// Accepted by the photo endpoint; mirrors the server's allow-list.
export const PHOTO_CONTENT_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
export const PHOTO_MAX_BYTES = 5 * 1024 * 1024;

export interface PhotoUploadResult {
  saved: boolean;
  photoFileId: string;
  photoUrl: string;
}

export interface StartResult {
  sent: boolean;
  // Returned outside production only — lets staging walk the flow without email.
  applyLink?: string;
  token?: string;
}

export interface SaveResult {
  saved: boolean;
  status: string;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      ...init,
      headers: { 'Content-Type': 'application/json', ...(init?.headers || {}) },
    });
  } catch (e) {
    throw new ApiError(`network: ${(e as Error).message}`, 0);
  }
  if (res.status === 429) {
    const retryAfter = Number(res.headers.get('Retry-After'));
    throw new RateLimited(Number.isFinite(retryAfter) ? retryAfter * 1000 : 3000);
  }
  if (!res.ok) {
    throw new ApiError(`http_${res.status}`, res.status);
  }
  return (await res.json()) as T;
}

// ---------------------------------------------------------------------------
// DEV mock — walk the whole flow with no API. Never bundled into behavior in
// production builds (guarded on import.meta.env.DEV).
// ---------------------------------------------------------------------------
const MOCK =
  import.meta.env.DEV && import.meta.env.VITE_MOCK_PROVIDER_API === '1';
const MOCK_KEY = 'expy.providerApply.mock';
const MOCK_TOKEN = 'mock-token';

function mockLoad(): ProviderApplication | null {
  try {
    const raw = window.localStorage.getItem(MOCK_KEY);
    return raw ? (JSON.parse(raw) as ProviderApplication) : null;
  } catch {
    return null;
  }
}
function mockSave(app: ProviderApplication): void {
  try {
    window.localStorage.setItem(MOCK_KEY, JSON.stringify(app));
  } catch {
    /* ignore */
  }
}

export function startApplication(email: string, handoff?: ClaimHandoff | null): Promise<StartResult> {
  if (MOCK) {
    const existing = mockLoad();
    if (!existing || existing.email !== email) {
      mockSave({ email, status: 'draft' });
    }
    return Promise.resolve({
      sent: true,
      token: MOCK_TOKEN,
      applyLink: `${window.location.origin}/providers/apply?token=${MOCK_TOKEN}`,
    });
  }
  return request<StartResult>(`/public/provider-application/start`, {
    method: 'POST',
    body: JSON.stringify({
      email,
      ...(handoff ? { npi: handoff.npi, country: handoff.country } : {}),
    }),
  });
}

export function getApplication(token: string): Promise<{ application: ProviderApplication }> {
  if (MOCK) {
    const app = mockLoad();
    if (token !== MOCK_TOKEN || !app) return Promise.reject(new ApiError('http_404', 404));
    if (app.status === 'submitted') return Promise.reject(new ApiError('http_410', 410));
    return Promise.resolve({ application: app });
  }
  return request<{ application: ProviderApplication }>(
    `/public/provider-application?token=${encodeURIComponent(token)}`,
  );
}

export function saveApplication(
  token: string,
  fields: ProviderApplicationFields,
  submit = false,
): Promise<SaveResult> {
  if (MOCK) {
    const app = mockLoad() || {};
    const next: ProviderApplication = { ...app, ...fields };
    if (submit) next.status = 'submitted';
    mockSave(next);
    return Promise.resolve({ saved: true, status: next.status || 'draft' });
  }
  return request<SaveResult>(`/public/provider-application`, {
    method: 'POST',
    body: JSON.stringify({ token, ...fields, ...(submit ? { submit: true } : {}) }),
  });
}

// Upload a headshot. Sends the RAW file bytes (not JSON, not base64) with the
// image's own Content-Type, matching the server's raw-body endpoint; the token
// rides the query string like the GET. Returns the stored key + a signed preview
// URL. Size/type are validated client-side here for a fast error, and again by
// the server, which is the real gate.
export async function uploadPhoto(token: string, file: File): Promise<PhotoUploadResult> {
  if (!PHOTO_CONTENT_TYPES.includes(file.type)) {
    throw new ApiError('unsupported_type', 415);
  }
  if (file.size > PHOTO_MAX_BYTES) {
    throw new ApiError('too_large', 413);
  }
  if (MOCK) {
    const app = mockLoad() || {};
    const photoFileId = `provider-photos/mock-${Date.now()}`;
    mockSave({ ...app, photoFileId });
    return Promise.resolve({ saved: true, photoFileId, photoUrl: URL.createObjectURL(file) });
  }
  let res: Response;
  try {
    res = await fetch(
      `${BASE}/public/provider-application/photo?token=${encodeURIComponent(token)}`,
      { method: 'POST', headers: { 'Content-Type': file.type }, body: file },
    );
  } catch (e) {
    throw new ApiError(`network: ${(e as Error).message}`, 0);
  }
  if (res.status === 429) {
    const retryAfter = Number(res.headers.get('Retry-After'));
    throw new RateLimited(Number.isFinite(retryAfter) ? retryAfter * 1000 : 3000);
  }
  if (!res.ok) {
    throw new ApiError(`http_${res.status}`, res.status);
  }
  return (await res.json()) as PhotoUploadResult;
}
